import { CloseOutlined } from '@ant-design/icons';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import {
  activeCompanySelector,
  selectActiveCompanyId,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import styles from './BillingPlans.module.css';
import { isEqual, uniqueId } from 'lodash';
import { useOpenConditionalDowngradeModal } from '../../../../../common/hooks/use-open-conditional-downgrade-modal';
import {
  AddonSettingsKeyType,
  EnhancedPlan,
} from '@mm/state/interfaces/subscription';
import {
  BillingPlanCardProps,
  BillingTabCategory,
  useBillingPlans,
} from './use-billing-plans';
import { Overlay } from '@mm/components/src/Overlay/Overlay';
import { BillingPriceTableModal } from '../SubscriptionTabsArea/ActiveSubscriptionTab/BillingPageCard/BillingPriceTableModal/BillingPriceTableModal';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { Plan } from './Plan';
import { PlanDescription } from './PlanDescription';
import { BillingGuarantee } from '../BillingGuarantee/BillingGuarantee';

type Props = {
  landmineOpenId?: number;
  onBillingSuccess?: () => void;
  hideFreePlan?: boolean;
  plans?: BillingPlanCardProps[]; // prop for controlled version
};

export const BillingPlans = ({
  plans,
  landmineOpenId,
  onBillingSuccess,
  hideFreePlan,
}: Props) => {
  const isShopify = useSelector(selectIsShopifyContext);
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );

  const companyId = useSelector(selectActiveCompanyId);
  const company = useSelector(activeCompanySelector);
  const isAgency = company?.companyAttributes?.agency;
  const [modalProps, setModalProps] = useState<
    { plan?: EnhancedPlan; type: AddonSettingsKeyType } | undefined
  >(undefined);

  const {
    billingPlanTabs,
    billingPlans,
    activeFacebookPage,
    currentPlan,
    tabId,
    pageId,
    plansToDowngrade,
    canCancelSubscription,
    isAwaitingCancellation,
    isManageSubscriptionsPage,
  } = useBillingPlans({
    isAnnual: false,
    onBillingSuccess,
    hideFreePlan,
    landmineOpenId,
  });
  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal();
  const { getPlans } = useSubscriptionCRUDActions();

  const getOptionParams = (option: BillingPlanCardProps) => {
    const downgrade = plansToDowngrade.find(plan =>
      option?.planSubtypes?.every(subtype =>
        plan?.planSubtypes.includes(subtype),
      ),
    );

    if (isShopify) {
      return {
        currentTag: option.currentTag,
        downgrade: undefined,
        buttonNameAdjusted: option.buttonName,
      };
    }

    const currentTag =
      currentPlan &&
      option?.planSubtypes?.every(subtype =>
        currentPlan?.planSubtypes.includes(subtype),
      );

    const hideCurrentTab = option.category !== 'other' && currentTag;
    const buttonNameAdjusted = currentTag
      ? 'Current Plan'
      : downgrade
      ? 'Downgrade'
      : option.buttonName;

    return {
      downgrade,
      currentTag,
      buttonNameAdjusted,
      hideCurrentTab,
    };
  };

  const visiblePlans = billingPlans.filter(option => !option.hide);
  const currentPlanInOptions = visiblePlans.find(
    p =>
      p.category !== 'other' &&
      isEqual(currentPlan?.planSubtypes, p.planSubtypes),
  );
  const filteredTabs = billingPlanTabs.filter(tab =>
    visiblePlans.find(p =>
      currentPlanInOptions
        ? tab.category !== 'other' && p.category === tab.category
        : p.category === tab.category,
    ),
  );

  const [activePlanTab, setActivePlanTab] = useState<BillingTabCategory>(
    filteredTabs[0]?.category,
  );

  const selectedTab = billingPlanTabs.find(
    tab => tab.category === activePlanTab,
  );
  const filteredPlans =
    plans ||
    visiblePlans.filter(option =>
      currentPlanInOptions
        ? option.category !== 'other' && option.category === activePlanTab
        : option.category === activePlanTab,
    );

  const isMetaRelatedPlan =
    activePlanTab === 'meta' || activePlanTab === 'metaEmail';

  const { subscription } = activeFacebookPage || {};

  const { openConditionalDowngradeModal } = useOpenConditionalDowngradeModal();

  const onCancelSubscription = () => {
    if (subscription && currentPlan) {
      openConditionalDowngradeModal({
        subscription,
        plan: currentPlan,
        facebookPage: activeFacebookPage,
        tabId,
      });
    }
  };

  useEffect(() => {
    if (!companyId) {
      return;
    }
    (async () => {
      if (!plansMapping) {
        await getPlans(companyId);
      }
    })();
  }, [plansMapping, companyId, getPlans]);

  const cancelSubscription =
    canCancelSubscription &&
    (isAwaitingCancellation ? (
      <div className={styles.cancellationInfo}>
        Awaiting cancellation approval
      </div>
    ) : (
      <PrimaryButton
        onClick={onCancelSubscription}
        className={styles.cancelButton}
        color="transparent"
      >
        <CloseOutlined />
        Cancel your plan
      </PrimaryButton>
    ));

  return (
    <div id="billing-plans" className={styles.container}>
      {cancelSubscription}

      <div
        className={classNames(styles.content, {
          [styles.contentWhite]:
            isManageSubscriptionsPage && filteredTabs.length > 1,
          [styles.contentWithTag]: isFreeTrialPromotionActive,
          [styles.agencyContent]: isAgency,
        })}
      >
        <div className={styles.tabsWrapper}>
          {filteredTabs.map(planTab => (
            <div
              className={classNames(styles.planTab, {
                [styles.activePlanTab]: planTab.category === activePlanTab,
                [styles.planTabFlexed]: filteredTabs.length > 1,
              })}
              key={planTab.category}
              onClick={() => setActivePlanTab(planTab.category)}
            >
              <img src={planTab.icon} />
              <span className={styles.tabName}>{planTab.name}</span>
            </div>
          ))}
        </div>
        <div className={styles.planWrapper}>
          {selectedTab?.mission && (
            <div className={styles.planMission}>{selectedTab.mission}</div>
          )}
          {filteredPlans.map(plan => (
            <div
              key={uniqueId(plan.title)}
              className={classNames(styles.planDescription, {
                [styles.planDescriptionWidthBadge]:
                  plan.category === 'meta' || plan.category === 'metaEmail',
              })}
            >
              <Plan option={plan} getOptionParams={getOptionParams} />
              <PlanDescription
                option={plan}
                className={classNames({
                  [styles.hiddenDescription]: activePlanTab === 'other',
                })}
                isAgency={isAgency}
              />
            </div>
          ))}
        </div>
      </div>
      {isMetaRelatedPlan && <BillingGuarantee />}
      {modalProps && (
        <Overlay className={styles.pricingOverlay}>
          <BillingPriceTableModal
            type={modalProps.type}
            pageId={activeFacebookPage?.id || Number(pageId)}
            plan={modalProps.plan}
            onClose={() => setModalProps(undefined)}
          />
        </Overlay>
      )}
    </div>
  );
};
